import { FunctionComponent, ReactElement, useState, useCallback } from "react";
import {
    Box,
    Button,
    Chip,
    Typography,
    TextField,
    MenuItem,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { styled } from "@mui/material/styles";
import {
    FileUpload as UploadIcon,
    FileDownload as DownloadIcon,
    NotificationsOutlined as NotifyIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth, useMessage } from "../../sdk/hooks";
import { formatCurrency } from "../../sdk/utils/numbers";
import {
    PriceChangeScheduleStatus,
    PriceChangeSchedulePlatform,
    priceChangeScheduleStatusNames,
    priceChangeSchedulePlatformNames,
} from "../../utils/enums";
import { useGetAllSchedules } from "../../hooks/price-change-schedules";
import { exportSchedulesCsv } from "../../sdk/utils/api/price-change-schedule";
import { DataGridFooter } from "../../components/common";
import { IPage } from "../../types";
import { BulkUploadDialog } from "./BulkUploadDialog";
import { NotificationSettingsDialog } from "./NotificationSettingsDialog";

const Root = styled("main")({
    display: "flex",
    flexDirection: "column",
});

const StyledDataGrid = styled(DataGrid)({
    "& .MuiDataGrid-cell:focus": { outline: "none" },
    "& .MuiDataGrid-cell:focus-within": { outline: "none" },
    "& .MuiDataGrid-row:hover": { cursor: "pointer" },
});

const statusColorMap: Record<PriceChangeScheduleStatus, "warning" | "success" | "default" | "error"> = {
    [PriceChangeScheduleStatus.SCHEDULED]: "warning",
    [PriceChangeScheduleStatus.ACTIVE]: "success",
    [PriceChangeScheduleStatus.COMPLETED]: "default",
    [PriceChangeScheduleStatus.CANCELLED]: "error",
};

const formatPaisa = (paisa: number) => formatCurrency(paisa / 100);

const formatDateTime = (dateStr: string) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

export const PriceChangeSchedulesScreen: FunctionComponent = (): ReactElement => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const navigate = useNavigate();
    const { token } = useAuth();
    const { showError } = useMessage();

    const [statusFilter, setStatusFilter] = useState<string>("all");
    const [uploadOpen, setUploadOpen] = useState(false);
    const [notifyOpen, setNotifyOpen] = useState(false);
    const [page, setPage] = useState<IPage>({
        limit: 20,
        cursor: null,
        direction: "after",
    });

    const statusParam = statusFilter !== "all" ? Number(statusFilter) : undefined;

    const queryKey = [
        "priceChangeSchedules",
        page.limit.toString(),
        page.cursor ?? "",
        page.direction,
        statusFilter,
    ];

    const { data, isLoading } = useGetAllSchedules(queryKey, {
        limit: page.limit,
        cursor: page.cursor ?? undefined,
        direction: page.direction,
        status: statusParam,
    });

    const records = data?.records ?? [];
    const cursors = data?.cursors ?? {};

    const handleNavigateBack = useCallback(() => {
        setPage((prev) => ({
            ...prev,
            cursor: cursors.previous ?? "",
            direction: "before",
        }));
    }, [cursors.previous]);

    const handleNavigateForward = useCallback(() => {
        setPage((prev) => ({
            ...prev,
            cursor: cursors.next ?? "",
            direction: "after",
        }));
    }, [cursors.next]);

    const handleStatusChange = (value: string) => {
        setStatusFilter(value);
        setPage({ limit: 20, cursor: null, direction: "after" });
    };

    const handleExport = async () => {
        if (!token) return;
        try {
            const params: any = {};
            if (statusParam !== undefined) params.status = statusParam;

            const csv = await exportSchedulesCsv(token, params);
            const blob = new Blob([csv], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `price-schedules-${new Date().toISOString().slice(0, 10)}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            showError("Failed to export schedules");
        }
    };

    const columns: GridColDef[] = [
        {
            field: "sku",
            headerName: "SKU",
            minWidth: 120,
            flex: 1,
            valueGetter: (params) => (params.row as any).variant?.sku ?? "—",
        },
        {
            field: "variantTitle",
            headerName: "Variant",
            minWidth: 150,
            flex: 1,
            valueGetter: (params) => (params.row as any).variant?.title ?? "—",
        },
        {
            field: "sellingPrice",
            headerName: "Scheduled Price",
            minWidth: 120,
            renderCell: (params) => formatPaisa(params.value),
        },
        {
            field: "baseSellingPrice",
            headerName: "Original Price",
            minWidth: 120,
            renderCell: (params) => formatPaisa(params.value),
        },
        {
            field: "startsAt",
            headerName: "Starts At",
            minWidth: 160,
            renderCell: (params) => formatDateTime(params.value),
        },
        {
            field: "endsAt",
            headerName: "Ends At",
            minWidth: 160,
            renderCell: (params) => formatDateTime(params.value),
        },
        {
            field: "status",
            headerName: "Status",
            minWidth: 110,
            renderCell: (params) => {
                const label = priceChangeScheduleStatusNames[params.value] || "Unknown";
                const color = statusColorMap[params.value as PriceChangeScheduleStatus] || "default";
                return <Chip label={label} color={color} size="small" />;
            },
        },
        {
            field: "platforms",
            headerName: "Platforms",
            minWidth: 140,
            renderCell: (params) => (
                <Box display="flex" gap={0.5}>
                    {params.value?.map((p: any) => (
                        <Chip
                            key={p.id}
                            label={priceChangeSchedulePlatformNames[p.platform] || p.platform}
                            size="small"
                            variant="outlined"
                        />
                    ))}
                </Box>
            ),
        },
    ];

    return (
        <Root sx={{ padding: { xs: 1, sm: 2, md: 3 }, gap: { xs: 1, sm: 2 } }}>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "stretch", sm: "center" },
                    gap: 1,
                }}
            >
                <Typography variant={isMobile ? "h6" : "h5"}>
                    Price Change Schedules
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                    <Button
                        variant="outlined"
                        size={isMobile ? "small" : "medium"}
                        startIcon={<NotifyIcon />}
                        onClick={() => setNotifyOpen(true)}
                    >
                        Notifications
                    </Button>
                    <Button
                        variant="outlined"
                        size={isMobile ? "small" : "medium"}
                        startIcon={<DownloadIcon />}
                        onClick={handleExport}
                    >
                        Export
                    </Button>
                    <Button
                        variant="contained"
                        size={isMobile ? "small" : "medium"}
                        startIcon={<UploadIcon />}
                        onClick={() => setUploadOpen(true)}
                    >
                        Upload
                    </Button>
                </Box>
            </Box>

            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 1,
                    alignItems: { xs: "stretch", sm: "center" },
                }}
            >
                <TextField
                    select
                    label="Status"
                    size="small"
                    value={statusFilter}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    sx={{ minWidth: { xs: "100%", sm: 150 } }}
                >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value={String(PriceChangeScheduleStatus.SCHEDULED)}>Scheduled</MenuItem>
                    <MenuItem value={String(PriceChangeScheduleStatus.ACTIVE)}>Active</MenuItem>
                    <MenuItem value={String(PriceChangeScheduleStatus.COMPLETED)}>Completed</MenuItem>
                    <MenuItem value={String(PriceChangeScheduleStatus.CANCELLED)}>Cancelled</MenuItem>
                </TextField>
            </Box>

            <StyledDataGrid
                rows={records}
                columns={columns}
                autoHeight
                loading={isLoading}
                disableRowSelectionOnClick
                onRowClick={(params) => navigate(`/price-schedules/${params.row.id}`)}
                hideFooter
                columnVisibilityModel={
                    isMobile
                        ? {
                              variantTitle: false,
                              baseSellingPrice: false,
                              startsAt: false,
                              endsAt: false,
                              platforms: false,
                          }
                        : {}
                }
                sx={{ minHeight: records.length > 0 ? 400 : "auto" }}
                slots={{
                    noRowsOverlay: () => (
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            height="100%"
                            py={4}
                        >
                            <Typography color="textSecondary" variant="body2">
                                No price change schedules found
                            </Typography>
                        </Box>
                    ),
                }}
            />

            <DataGridFooter
                onNavigateBack={handleNavigateBack}
                onNavigateForward={handleNavigateForward}
                hasPreviousRecords={Boolean(cursors.previous)}
                hasNextRecords={Boolean(cursors.next)}
            />

            <BulkUploadDialog
                open={uploadOpen}
                onClose={() => setUploadOpen(false)}
            />

            <NotificationSettingsDialog
                open={notifyOpen}
                onClose={() => setNotifyOpen(false)}
            />
        </Root>
    );
};
